// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
// RxJS
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
// Models
import { SellingChannelModel } from '../../_models/selling-channel.model';

import { HelperService } from '../helper.service';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { StoreModel } from 'src/app/_models/store.model';
import IStoreWarehouseLink from '../../_interfaces/i-store-warehouse-link';
import IShipStationWarehouseMapping from '../../_interfaces/i-shipstation-warehouse-mapping';
import { UpdateStoreWarehousesRequestModel } from 'src/app/_models/update-store-warehouses-request-model';
import { Params } from '@angular/router';

const API_SELLINGCHANNELS_URL = environment.baseUrl + '/sellingChannels';

@Injectable()
export class SellingChannelsService {
    constructor(
        private http: HttpClient,
        private helperService: HelperService,
        private toastr: ToastrService,
        private translate: TranslateService) {
    }

    createStore(name: string, currencyId: number): Observable<any> {
        return this.http.post(`${API_SELLINGCHANNELS_URL}/create`, {},
            { headers: this.helperService.getHTTPHeaders(), params: { name, currencyId } as any })
            .pipe(catchError(this.handleError('createStore')));
    }

    updateStore(store: StoreModel): Observable<any> {
        return this.http.put(`${API_SELLINGCHANNELS_URL}/${store.id}`, store,
            { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError('updateStore')));
    }

    updateStoreWarehouses(request: UpdateStoreWarehousesRequestModel): Observable<any> {
        return this.http.post(`${API_SELLINGCHANNELS_URL}/updateStoreWarehouses`, request, { headers: this.helperService.getHTTPHeaders() });
    }

    walmartConnect(walmartStoreName: any, clientID: any, clientSecret: any, walmartStoreCurrency: any, storeId: any, _onlyActiveProducts: any, isCanadaStore: boolean, _isTestAccount: any): Observable<any> {
        let walmartReqBody = {
            WalmartStoreName: walmartStoreName,
            ClientId: clientID,
            ClientSecret: clientSecret,
            WalmartStoreCurrency: walmartStoreCurrency,
            id: storeId,
            onlyActiveProducts: _onlyActiveProducts,
            isCanadaStore: isCanadaStore,
            isTestAccount: _isTestAccount
        };
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/walmartConnect`, walmartReqBody, { headers: this.helperService.getHTTPHeaders() });
    }

    walmartDSVConnect(storeId: any, walmartStoreName: any, walmartStoreCurrency: any, consumerId: any, privateKey: any, consumerChannelType: any, shipNode: any, _onlyActiveProducts: any, _isTestAccount: any): Observable<any> {
        const walmartDSVReqBody = {
            id: storeId,
            WalmartStoreName: walmartStoreName,
            WalmartStoreCurrency: walmartStoreCurrency,
            ConsumerId: consumerId,
            PrivateKey: privateKey,
            ConsumerChannelType: consumerChannelType,
            ShipNode: shipNode,
            onlyActiveProducts: _onlyActiveProducts,
            isTestAccount: _isTestAccount
        };
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/walmartDsvConnect`, walmartDSVReqBody, { headers: this.helperService.getHTTPHeaders() });
    }

    walmartCanadaConnect(storeId: any, walmartStoreName: any, consumerId: any, privateKey: any, consumerChannelType: any, _onlyActiveProducts: any, _isTestAccount: any): Observable<any> {
        const walmartDSVReqBody = {
            id: storeId,
            WalmartStoreName: walmartStoreName,
            ConsumerId: consumerId,
            PrivateKey: privateKey,
            ConsumerChannelType: consumerChannelType,
            onlyActiveProducts: _onlyActiveProducts,
            isTestAccount: _isTestAccount
        };
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/walmartCanadaConnect`, walmartDSVReqBody, { headers: this.helperService.getHTTPHeaders() });
    }

    createWalmartStoreConnection(payload: {
        name?: string,
        onlyActiveProducts: boolean,
        //isCanadaStore: boolean,
        id?: number,
        code: string,
        redirectUri: string,
        sellerId: string,
        isTestAccount: boolean,
        determineStoreNameInBackend?: boolean,
        currencyId?: number
    }): Observable<any> {
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/createWalmartConnection`, payload, { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError<any>('createWalmartConnection')));
    }

    bigCommerceConnect(name: any, accessToken: any, clientId: any, clientSecret: any, apiPath: any, onlyActiveProducts: any, storeId: any): Observable<any> {
        if (!storeId) {
            storeId = '';
        }
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/bigCommerceConnect?bigCommerceStoreName=${name}&accessToken=${accessToken}&clientID=${clientId}&clientSecret=${clientSecret}&apiPath=${apiPath}&onlyActiveProducts=${onlyActiveProducts}&id=${storeId}`, {}, { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError<any>('bigCommerceConnect')));
    }

    checkEbayStores(name: any, email: any): Observable<any> {
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/ebaycheck?name=${name}&email=${email}`, {}, { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError<any>('checkEbayStores')));
    }

    updateEbayStore(storeId: any, name: any, email: any, _onlyActiveProducts: boolean, onlyItemsWSkus: boolean): Observable<any> {
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/updateEbayStore`, {
            storeName: name,
            id: storeId,
            onlyActiveProducts: _onlyActiveProducts,
            onlyItemsWSkus: onlyItemsWSkus
        }, { headers: this.helperService.getHTTPHeaders() });
    }

    createEbayStoreConnection(nameParam: any, emailParam: any, codeParam: any, _onlyActiveProducts: boolean, storeId: number = null, onlyItemsWSkus: boolean): Observable<any> {
        if (!storeId) {
            storeId = null;
        }
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/createEbayConnection`, {
            name: nameParam,
            email: emailParam,
            code: codeParam,
            id: storeId,
            onlyActiveProducts: _onlyActiveProducts,
            onlyItemsWSkus: onlyItemsWSkus
        }, { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError<any>('createEbayConnection')));
    }

    createOverstockStoreConnection(name: any, username: any, password: any, warehouseName: any, onlyActiveProducts: any, storeId: any): Observable<any> {
        if (!storeId) {
            storeId = '';
        }
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/overstock-connect?name=${name}&username=${username}&password=${password}&warehouseName=${warehouseName}&onlyActiveProducts=${onlyActiveProducts}&id=${storeId}`, {}, { headers: this.helperService.getHTTPHeaders() });
    }

    createAcendaConnection(payload): Observable<any> {
        let params: Params = new HttpParams();
        for (const key in payload) {
            params = params.append(key, payload[key]);
        }
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/connect-target`, null, { params });
    }

    getTemuAuthConfig(): Observable<any> {
        return this.http.get<any>(`${API_SELLINGCHANNELS_URL}/temu-auth-config`, { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError<any>('getTemuAuthConfig')));
    }

    reauthorizeTemu(storeId: number, code: string): Observable<{ saved: boolean; storeId: number }> {
        return this.http.post<{ saved: boolean; storeId: number }>(`${API_SELLINGCHANNELS_URL}/reauthorize-temu`, { storeId, code });
    }

    createTemuConnection(payload): Observable<any> {
        let params: Params = new HttpParams();
        for (const key in payload) {
            if (payload[key] === null || payload[key] === undefined || payload[key] === '') continue;
            params = params.append(key, payload[key]);
        }
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/connect-temu`, null, { params });
    }

    getTikTokOAuthConfig(): Observable<any> {
        return this.http.get<any>(`${API_SELLINGCHANNELS_URL}/tiktok-oauth/config`);
    }

    startTikTokOAuth(payload): Observable<any> {
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/tiktok-oauth/start`, payload);
    }

    exchangeTikTokOAuth(payload): Observable<any> {
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/tiktok-oauth/exchange`, payload);
    }

    completeTikTokOAuth(payload): Observable<any> {
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/tiktok-oauth/complete`, payload);
    }

    verifyTikTokReadAccess(storeId: number): Observable<any> {
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/tiktok-oauth/verify/${storeId}`, {});
    }

    getTikTokAuthConfig(): Observable<any> {
        return this.http.get<any>(`${API_SELLINGCHANNELS_URL}/tiktok-auth-config`, { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError<any>('getTikTokAuthConfig')));
    }

    createTikTokConnection(payload): Observable<any> {
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/connect-tiktok`, payload, { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError<any>('connectTikTok')));
    }

    createMichaelsConnection(payload): Observable<any> {
        let params: Params = new HttpParams();
        for (const key in payload) {
            params = params.append(key, payload[key]);
        }
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/connect-michaels`, null, { params });
    }

    createAmazonAWSConnection(amazonAwsAccessKey: any, amazonAwsSecretKey: any, associateTag: any): Observable<any> {
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/amazon-aws-connect?amazonAwsAccessKey=${amazonAwsAccessKey}&amazonAwsSecretKey=${amazonAwsSecretKey}&associateTag=${associateTag}`, {}, { headers: this.helperService.getHTTPHeaders() });
    }

    delete(id: number) {
        return this.http.delete(`${API_SELLINGCHANNELS_URL}/items/${id}`,
            { headers: this.helperService.getHTTPHeaders() }).pipe(
                catchError(this.handleError<SellingChannelModel>('delete'))
            );
    }

    deleteWalmartConnection(storeId: number): Observable<any> {
        return this.http.post<any>(
            `${API_SELLINGCHANNELS_URL}/deleteWalmartConnection?storeId=${storeId}`,
            {},
            { headers: this.helperService.getHTTPHeaders() }
        ).pipe(
            catchError(this.handleError<any>('deleteWalmartConnection'))
        );
    }

    connectAmazon(data: {}): Observable<unknown> {
        return this.http.post(`${API_SELLINGCHANNELS_URL}/connectAmazon`, data, { headers: this.helperService.getHTTPHeaders() })
            .pipe(
                catchError(this.helperService.handleError('connectAmazon'))
            );
    }

    etsyConnect(etsyStoreName: any, storeName: any, storeId: any, onlyActiveProducts: any): Observable<any> {
        if (!storeId) {
            storeId = '';
        }
        return this.http.post(`${API_SELLINGCHANNELS_URL}/etsyconnect?storeName=${storeName}&etsyStoreName=${etsyStoreName}&id=${storeId}&onlyActiveProducts=${onlyActiveProducts}`, {}, { headers: this.helperService.getHTTPHeaders() });
    }

    etsyConnectV3(etsyStoreName: any, storeName: any, storeId: any, onlyActiveProducts: any): Observable<any> {
        if (!storeId) {
            storeId = '';
        }
        return this.http.post(`${API_SELLINGCHANNELS_URL}/etsyconnect/v3?storeName=${storeName}&etsyStoreName=${etsyStoreName}&id=${storeId}&onlyActiveProducts=${onlyActiveProducts}`, {}, { headers: this.helperService.getHTTPHeaders() });
    }

    wishConnect(storeName: any, storeId: any, _onlyActiveProducts: any, _code: any): Observable<any> {
        if (!storeId) {
            storeId = '';
        }
        const wishReqBody = {
            name: storeName,
            code: _code,
            id: storeId,
            onlyActiveProducts: _onlyActiveProducts
        };
        return this.http.post(`${API_SELLINGCHANNELS_URL}/createWishConnection`, wishReqBody, { headers: this.helperService.getHTTPHeaders() });
    }

    wayfairConnect(store: any, id: number = null) {
        return this.http.post(`${API_SELLINGCHANNELS_URL}/wayfairConnect`,
            {
                id: id, name: store.name, onlyActiveProducts: store.onlyActiveProducts,
                wayfairStore: {
                    key: store.key,
                    secret: store.secret,
                    sandboxKey: store.sandboxKey,
                    sandboxSecret: store.sandboxSecret,
                    wayfairWarehouseId: store.wayfairWarehouseId,
                    commissionRate: store.commissionRate != null ? store.commissionRate / 100 : null
                }
            },
            { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError('wayfairConnect')));
    }

    rainforestApiConnect(rainforestApi: any, id: number = null) {
        return this.http.post(`${API_SELLINGCHANNELS_URL}/rainforestApiConnect`,
            { id: rainforestApi.id, name: rainforestApi.name, key: rainforestApi.key },
            { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError('rainforestApiConnect')));
    }

    shopifyVerify(code: any, hmac: any, shop: any, timestamp: any): Observable<any> {
        return this.http.get(`${API_SELLINGCHANNELS_URL}/verifyShopify?code=${code}&hmac=${hmac}&shop=${shop}&timestamp=${timestamp}`, { headers: this.helperService.getHTTPHeaders() });
    }

    getAllChannels(): Observable<any> {
        return this.http.get(API_SELLINGCHANNELS_URL, { headers: this.helperService.getHTTPHeaders() })
            .pipe(catchError(this.handleError('getAllChannels')));
    }

    jetConnect(jetStoreName: any, apiUser: any, secret: any, jetStoreCurrency: any, onlyActiveProducts: any, storeId: any): Observable<any> {
        if (!storeId) {
            storeId = '';
        }
        return this.http.post(`${API_SELLINGCHANNELS_URL}/jet-connect?jetStoreName=${jetStoreName}&apiUser=${apiUser}&secret=${secret}&jetStoreCurrency=${jetStoreCurrency}&onlyActiveProducts=${onlyActiveProducts}&id=${storeId}`, {}, { headers: this.helperService.getHTTPHeaders() });
    }

    // The key and secret go in the body, not the query string: they are credentials, and query
    // strings are kept by browser history, proxies and web-server access logs.
    shipstationConnect(apiKey: any, apiSecret: any, ShipStationaccountName: any, storeId: any, onlyActiveProducts: any): Observable<any> {
        const shipStationReqBody = {
            // A new connection has no store yet; null is what the old empty `id=` query value bound to.
            id: storeId ? storeId : null,
            shipStationAccountName: ShipStationaccountName,
            apiKey: apiKey,
            apiSecret: apiSecret,
            onlyActiveProducts: onlyActiveProducts
        };
        return this.http.post(`${API_SELLINGCHANNELS_URL}/shipstation-connect`, shipStationReqBody, { headers: this.helperService.getHTTPHeaders() });
    }

    etsyVerify(_oauthToken: any, _oauth_verifier: any): Observable<any> {
        return this.http.post(`${API_SELLINGCHANNELS_URL}/etsyverify`, {
            oauth_token: _oauthToken,
            oauth_verifier: _oauth_verifier
        }, { headers: this.helperService.getHTTPHeaders() });
    }

    etsyVerifyV3(_code: any, _state: any): Observable<any> {
        return this.http.post(`${API_SELLINGCHANNELS_URL}/etsyverify/v3`, {
            code: _code,
            state: _state
        }, { headers: this.helperService.getHTTPHeaders() });
    }

    amazonAdvertisingVerify(code: any, AmazonStoreId: any): Observable<any> {
        return this.http.post(`${API_SELLINGCHANNELS_URL}/amazon-advertising-connect?code=${code}&AmazonStoreId=${AmazonStoreId}`, {}, { headers: this.helperService.getHTTPHeaders() });
    }

    changeStoreLocation(storeId: any, storeLocationId: any): Observable<any> {
        let changeStoreLocationBody = { StoreId: storeId, storeLocationId: storeLocationId };
        return this.http.post<any>(`${API_SELLINGCHANNELS_URL}/changeStoreLocation`, changeStoreLocationBody, { headers: this.helperService.getHTTPHeaders() });
    }

    getStore(storeId): Observable<any> {
        return this.http.get(`${API_SELLINGCHANNELS_URL}/getStore/${storeId}`, { headers: this.helperService.getHTTPHeaders() });
    }

    getStoreWarehouseLocationLinks(): Observable<IStoreWarehouseLink[]> {
        return this.http.get<IStoreWarehouseLink[]>(`${API_SELLINGCHANNELS_URL}/GetStoreWarehouseLinks`);
    }

    getStoreWarehouseLinks(storeId: number): Observable<IStoreWarehouseLink[]> {
        return this.http.get<IStoreWarehouseLink[]>(
            `${API_SELLINGCHANNELS_URL}/${storeId}/StoreWarehouseLinks`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    createStoreWarehouseLink(storeId: number, warehouseId: number, storeLocationId?: number): Observable<IStoreWarehouseLink[]> {
        return this.http
            .post<IStoreWarehouseLink[]>(
                `${API_SELLINGCHANNELS_URL}/createStoreWarehouseLink?storeId=${storeId}&warehouseid=${warehouseId}&storeLocationId=${storeLocationId}`,
                {},
                { headers: this.helperService.getHTTPHeaders() }
            );
    }

    deleteStoreWarehouseLink(storeId: number, warehouseId: number, storeLocationId?: number): Observable<IStoreWarehouseLink[]> {
        return this.http
            .delete<IStoreWarehouseLink[]>(
                `${API_SELLINGCHANNELS_URL}/DeleteStoreWarehouseLink?storeId=${storeId}&warehouseid=${warehouseId}&storeLocationId=${storeLocationId}`,
                { headers: this.helperService.getHTTPHeaders() }
            );
    }

    getShopifyLocationMappings(storeId: number): Observable<IStoreWarehouseLink[]> {
        return this.http.get<IStoreWarehouseLink[]>(
            `${API_SELLINGCHANNELS_URL}/${storeId}/ShopifyLocationMappings`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    setShopifyLocationMapping(storeId: number, shopifyLocationId: string, warehouseId: number): Observable<any> {
        return this.http.post<any>(
            `${API_SELLINGCHANNELS_URL}/SetShopifyLocationMapping?storeId=${storeId}&shopifyLocationId=${encodeURIComponent(shopifyLocationId)}&warehouseId=${warehouseId}`,
            {},
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    deleteShopifyLocationMapping(storeId: number, shopifyLocationId: string): Observable<any> {
        return this.http.delete<any>(
            `${API_SELLINGCHANNELS_URL}/DeleteShopifyLocationMapping?storeId=${storeId}&shopifyLocationId=${encodeURIComponent(shopifyLocationId)}`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    // ShipStation warehouse -> IA warehouse. The nightly sync only ever links these two by an exact
    // name match, so any account whose names differ needs the mapping set here or shipped orders
    // never deduct inventory (MN-3789).
    getShipStationWarehouses(): Observable<IShipStationWarehouseMapping[]> {
        return this.http.get<IShipStationWarehouseMapping[]>(
            `${API_SELLINGCHANNELS_URL}/ShipStationWarehouses`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    setShipStationWarehouseMapping(shipStationWarehouseId: number, warehouseId: number): Observable<any> {
        return this.http.post<any>(
            `${API_SELLINGCHANNELS_URL}/SetShipStationWarehouseMapping?shipStationWarehouseId=${shipStationWarehouseId}&warehouseId=${warehouseId}`,
            {},
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    // ShipStation inventory routing (MN-3830). Inventory is pushed only when routing is enabled
    // AND at least one Inventory Rule is mapped to a ShipStation inventory location, so both the
    // flag and the mappings are read before the screen claims anything is syncing.
    getShipStationInventoryLocations(storeId: number): Observable<any[]> {
        return this.http.get<any[]>(
            `${API_SELLINGCHANNELS_URL}/${storeId}/ShipStationInventoryLocations`,
            { headers: this.helperService.getHTTPHeaders() });
    }

    getShipStationLinkRuleLocations(storeId: number): Observable<any[]> {
        return this.http.get<any[]>(
            `${API_SELLINGCHANNELS_URL}/${storeId}/ShipStationLinkRuleLocations`,
            { headers: this.helperService.getHTTPHeaders() });
    }

    setShipStationLinkRuleLocation(storeId: number, linkRuleId: number, storeLocationId: number): Observable<any> {
        return this.http.post(
            `${API_SELLINGCHANNELS_URL}/SetShipStationLinkRuleLocation?storeId=${storeId}&linkRuleId=${linkRuleId}&storeLocationId=${storeLocationId}`,
            {}, { headers: this.helperService.getHTTPHeaders() });
    }

    deleteShipStationLinkRuleLocation(storeId: number, linkRuleId: number): Observable<any> {
        return this.http.delete(
            `${API_SELLINGCHANNELS_URL}/DeleteShipStationLinkRuleLocation?storeId=${storeId}&linkRuleId=${linkRuleId}`,
            { headers: this.helperService.getHTTPHeaders() });
    }

    getShipStationUseLinkRuleLocationRouting(storeId: number): Observable<any> {
        return this.http.get<any>(
            `${API_SELLINGCHANNELS_URL}/GetShipStationUseLinkRuleLocationRouting?storeId=${storeId}`,
            { headers: this.helperService.getHTTPHeaders() });
    }

    setShipStationUseLinkRuleLocationRouting(storeId: number, enabled: boolean): Observable<any> {
        return this.http.post(
            `${API_SELLINGCHANNELS_URL}/SetShipStationUseLinkRuleLocationRouting?storeId=${storeId}&enabled=${enabled}`,
            {}, { headers: this.helperService.getHTTPHeaders() });
    }

    // The key goes in the body, not the query string: it is a credential, and query strings are kept
    // by browser history, proxies and web-server access logs. An empty key clears the stored one.
    setShipStationInventoryApiKey(shipStationUserId: number, inventoryApiKey: string): Observable<any> {
        return this.http.post(
            `${API_SELLINGCHANNELS_URL}/SetShipStationInventoryApiKey`,
            { shipStationUserId, inventoryApiKey },
            { headers: this.helperService.getHTTPHeaders() });
    }

    deleteShipStationWarehouseMapping(shipStationWarehouseId: number): Observable<any> {
        return this.http.delete<any>(
            `${API_SELLINGCHANNELS_URL}/DeleteShipStationWarehouseMapping?shipStationWarehouseId=${shipStationWarehouseId}`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }


    // Target/Acenda: LinkRule -> StoreLocation (Acenda warehouse) routing
    getTargetLinkRuleLocations(storeId: number): Observable<any[]> {
        return this.http.get<any[]>(
            `${API_SELLINGCHANNELS_URL}/${storeId}/TargetLinkRuleLocations`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    setTargetLinkRuleLocation(storeId: number, linkRuleId: number, storeLocationId: number): Observable<any> {
        return this.http.post<any>(
            `${API_SELLINGCHANNELS_URL}/SetTargetLinkRuleLocation?storeId=${storeId}&linkRuleId=${linkRuleId}&storeLocationId=${storeLocationId}`,
            {},
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    deleteTargetLinkRuleLocation(storeId: number, linkRuleId: number): Observable<any> {
        return this.http.delete<any>(
            `${API_SELLINGCHANNELS_URL}/DeleteTargetLinkRuleLocation?storeId=${storeId}&linkRuleId=${linkRuleId}`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    getTargetUseLinkRuleLocationRouting(storeId: number): Observable<{ enabled: boolean }> {
        return this.http.get<{ enabled: boolean }>(
            `${API_SELLINGCHANNELS_URL}/GetTargetUseLinkRuleLocationRouting?storeId=${storeId}`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    setTargetUseLinkRuleLocationRouting(storeId: number, enabled: boolean): Observable<any> {
        return this.http.post<any>(
            `${API_SELLINGCHANNELS_URL}/SetTargetUseLinkRuleLocationRouting?storeId=${storeId}&enabled=${enabled}`,
            {},
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    // Shopify: LinkRule -> Shopify StoreLocation routing
    getShopifyLinkRuleLocations(storeId: number): Observable<any[]> {
        return this.http.get<any[]>(
            `${API_SELLINGCHANNELS_URL}/${storeId}/ShopifyLinkRuleLocations`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    setShopifyLinkRuleLocation(storeId: number, linkRuleId: number, storeLocationId: number): Observable<any> {
        return this.http.post<any>(
            `${API_SELLINGCHANNELS_URL}/SetShopifyLinkRuleLocation?storeId=${storeId}&linkRuleId=${linkRuleId}&storeLocationId=${storeLocationId}`,
            {},
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    deleteShopifyLinkRuleLocation(storeId: number, linkRuleId: number): Observable<any> {
        return this.http.delete<any>(
            `${API_SELLINGCHANNELS_URL}/DeleteShopifyLinkRuleLocation?storeId=${storeId}&linkRuleId=${linkRuleId}`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    getShopifyUseLinkRuleLocationRouting(storeId: number): Observable<{ enabled: boolean }> {
        return this.http.get<{ enabled: boolean }>(
            `${API_SELLINGCHANNELS_URL}/GetShopifyUseLinkRuleLocationRouting?storeId=${storeId}`,
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    setShopifyUseLinkRuleLocationRouting(storeId: number, enabled: boolean): Observable<any> {
        return this.http.post<any>(
            `${API_SELLINGCHANNELS_URL}/SetShopifyUseLinkRuleLocationRouting?storeId=${storeId}&enabled=${enabled}`,
            {},
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    setStoreLocationInventorySyncExclusion(storeId: number, storeLocationId: number, excluded: boolean): Observable<any> {
        return this.http.post<any>(
            `${API_SELLINGCHANNELS_URL}/SetStoreLocationInventorySyncExclusion?storeId=${storeId}&storeLocationId=${storeLocationId}&excluded=${excluded}`,
            {},
            { headers: this.helperService.getHTTPHeaders() }
        );
    }

    private handleError<T>(operation = 'operation', result?: T) {

        return (error: any): Observable<T> => {
            switch (error.status) {
                case 401:
                    window.location.href = '/login';
                    break;
                case 422:
                    // This controller returns 422 three ways: { reason }, { message } and a bare
                    // string. Anything non-string would render as "[object Object]", so fall back
                    // to the generic message rather than showing the raw body.
                    const body = error?.error;
                    const message = body?.reason ?? body?.message ?? (typeof body === 'string' ? body : null);
                    this.toastr.error(message || this.translate.instant('GENERAL.MESSAGES.ERROR'), null, { timeOut: 0 });
                    break;
                default:
                    this.toastr.error(this.translate.instant('GENERAL.MESSAGES.ERROR'));
            }
            return throwError(error);
        };
    }
}
